import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    
    // Check if mobile device based on screen width
    const isMobile = window.innerWidth <= 768;
    
    // Particle settings
    const particlesArray = [];
    const numberOfParticles = isMobile ? 70 : 150; // Increased particles for richer effect
    
    // Black background
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Mouse interaction
    const mouse = {
      x: null,
      y: null,
      radius: 150
    };
    
    window.addEventListener('mousemove', function(event) {
      mouse.x = event.x;
      mouse.y = event.y;
    });
    
    // Touch support for mobile
    window.addEventListener('touchmove', function(event) {
      if (event.touches.length > 0) {
        mouse.x = event.touches[0].clientX;
        mouse.y = event.touches[0].clientY;
      }
    });
    
    // Create particle class
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 4 + 1;
        this.baseSize = this.size;
        this.density = Math.random() * 30 + 1;
        
        // Variable speeds for more natural movement
        this.speedX = (Math.random() - 0.5) * 1.2;
        this.speedY = (Math.random() - 0.5) * 1.2;
        
        // Create depth effect with z-index simulation
        this.z = Math.random() * 10;
        
        // Premium color palette with subtle variations
        const colorScheme = [
          // Bright blue-white
          { r: 210, g: 230, b: 255, a: 0.8 + Math.random() * 0.2 },
          // Subtle blue
          { r: 180, g: 200, b: 255, a: 0.7 + Math.random() * 0.3 },
          // Soft purple tint
          { r: 200, g: 190, b: 255, a: 0.7 + Math.random() * 0.3 },
          // Premium silver
          { r: 220, g: 220, b: 240, a: 0.8 + Math.random() * 0.2 }
        ];
        
        // Select random color from scheme
        const selectedColor = colorScheme[Math.floor(Math.random() * colorScheme.length)];
        this.color = `rgba(${selectedColor.r}, ${selectedColor.g}, ${selectedColor.b}, ${selectedColor.a})`;
        
        // Glow effect for some particles (premium touch)
        this.glow = Math.random() > 0.8;
        this.glowColor = `rgba(${selectedColor.r}, ${selectedColor.g}, ${selectedColor.b}, 0.15)`;
        this.glowSize = this.size * (2 + Math.random() * 2);
      }
      
      draw() {
        // Draw glow effect for premium particles
        if (this.glow) {
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.glowSize, 0, Math.PI * 2);
          ctx.fillStyle = this.glowColor;
          ctx.fill();
        }
        
        // Draw main particle
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
        
        // Add subtle highlight for depth (premium effect)
        if (this.z > 7) {
          ctx.beginPath();
          ctx.arc(this.x - this.size/3, this.y - this.size/3, this.size/4, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
          ctx.fill();
        }
      }
      
      update() {
        // Check if particles are close to mouse
        const dx = mouse.x - this.x;
        const dy = mouse.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Enhanced mouse interaction with glow effect
        if (mouse.x && mouse.y && distance < mouse.radius * 1.2) {
          const forceDirectionX = dx / distance;
          const forceDirectionY = dy / distance;
          const maxDistance = mouse.radius * 1.2;
          const force = (maxDistance - distance) / maxDistance;
          
          // Z-factor makes particles at different depths react differently
          const zFactor = (10 - this.z) / 10;
          const directionX = forceDirectionX * force * this.density * zFactor;
          const directionY = forceDirectionY * force * this.density * zFactor;
          
          this.x -= directionX * 0.8; // Smoother movement
          this.y -= directionY * 0.8;
          
          // Smooth size transition with depth effect
          const targetSize = this.baseSize + 2 * zFactor;
          this.size += (targetSize - this.size) * 0.1; // Smooth transition
          
          // Make particles near mouse glow brighter
          if (distance < mouse.radius * 0.6) {
            // Temporary glow for all particles near mouse
            this.glow = true;
            
            // Calculate glow intensity based on proximity to mouse
            const glowIntensity = 1 - (distance / (mouse.radius * 0.6));
            this.glowSize = this.size * (3 + glowIntensity * 3);
            
            // Extract color components
            const colorMatch = this.color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
            if (colorMatch) {
              const r = parseInt(colorMatch[1]);
              const g = parseInt(colorMatch[2]);
              const b = parseInt(colorMatch[3]);
              const a = parseFloat(colorMatch[4]);
              
              // Make particles whiter near mouse
              const whiteFactor = glowIntensity * 0.7;
              const newR = Math.min(255, r + (255 - r) * whiteFactor);
              const newG = Math.min(255, g + (255 - g) * whiteFactor);
              const newB = Math.min(255, b + (255 - b) * whiteFactor);
              
              // Update color
              this.color = `rgba(${newR}, ${newG}, ${newB}, ${a})`;
              
              // Update glow color
              this.glowColor = `rgba(${newR}, ${newG}, ${newB}, ${0.2 + glowIntensity * 0.3})`;
            }
          }
        } else {
          // Smooth size decrease
          this.size += (this.baseSize - this.size) * 0.05;
          
          // Reset glow size
          if (this.glow) {
            const targetGlowSize = this.size * (2 + Math.random() * 2);
            this.glowSize += (targetGlowSize - this.glowSize) * 0.05;
          }
        }
        
        // Add slight random movement for more natural flow
        this.speedX += (Math.random() - 0.5) * 0.01;
        this.speedY += (Math.random() - 0.5) * 0.01;
        
        // Limit max speed for smoother motion
        this.speedX = Math.max(-1.5, Math.min(1.5, this.speedX));
        this.speedY = Math.max(-1.5, Math.min(1.5, this.speedY));
        
        // Move particles with depth-based speed (z-index simulation)
        const depthFactor = (10 - this.z) / 10;
        this.x += this.speedX * depthFactor;
        this.y += this.speedY * depthFactor;
        
        // Improved boundary check with smoother transitions
        if (this.x < 0) {
          this.x = 0;
          this.speedX = Math.abs(this.speedX) * 0.8; // Reduce bounce intensity
        } else if (this.x > canvas.width) {
          this.x = canvas.width;
          this.speedX = -Math.abs(this.speedX) * 0.8;
        }
        
        if (this.y < 0) {
          this.y = 0;
          this.speedY = Math.abs(this.speedY) * 0.8;
        } else if (this.y > canvas.height) {
          this.y = canvas.height;
          this.speedY = -Math.abs(this.speedY) * 0.8;
        }
        
        this.draw();
      }
    }
    
    // Canvas sizing
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Reset particles when resizing
      particlesArray.length = 0;
      init();
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    // Create particles
    function init() {
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    }
    
    // Connect particles with premium lines
    function connect() {
      const maxDistance = 200; // Increased connection distance
      
      for (let a = 0; a < particlesArray.length; a++) {
        // Limit connections per particle for performance
        let connections = 0;
        const maxConnections = 5;
        
        for (let b = a; b < particlesArray.length; b++) {
          // Skip if we've reached max connections for this particle
          if (connections >= maxConnections) continue;
          
          const dx = particlesArray[a].x - particlesArray[b].x;
          const dy = particlesArray[a].y - particlesArray[b].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Calculate z-distance for depth effect
          const zDistance = Math.abs(particlesArray[a].z - particlesArray[b].z);
          
          // Connect only if both distance and z-distance conditions are met
          if (distance < maxDistance && zDistance < 5) {
            connections++;
            
            // Calculate opacity based on distance and z-factor
            const opacity = 1 - (distance / maxDistance);
            const zFactor = 1 - (zDistance / 5);
            const finalOpacity = opacity * zFactor * 0.5;
            
            // Determine line color based on particle colors
            const particleA = particlesArray[a];
            const particleB = particlesArray[b];
            
            // Premium gradient line effect
            const gradient = ctx.createLinearGradient(
              particleA.x, particleA.y, 
              particleB.x, particleB.y
            );
            
            // Extract colors from particles for gradient
            gradient.addColorStop(0, particleA.color);
            gradient.addColorStop(1, particleB.color);
            
            // Draw the connection with premium styling
            ctx.beginPath();
            ctx.moveTo(particleA.x, particleA.y);
            ctx.lineTo(particleB.x, particleB.y);
            
            // Apply gradient with opacity
            ctx.strokeStyle = gradient;
            ctx.globalAlpha = finalOpacity;
            
            // Line width based on particle sizes and z-index
            const avgSize = (particleA.size + particleB.size) / 2;
            ctx.lineWidth = avgSize * 0.15 * zFactor;
            
            ctx.stroke();
            ctx.globalAlpha = 1.0; // Reset global alpha
          }
        }
      }
    }
    
    // Animation loop with mouse glow effect
    function animate() {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Redraw black background
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add subtle pulsing effect to background
      const time = Date.now() * 0.0005;
      const pulseOpacity = 0.03 + Math.sin(time) * 0.01;
      
      // Draw white glow effect around mouse cursor
      if (mouse.x && mouse.y) {
        // Create white light glow around mouse
        const mouseGlow = ctx.createRadialGradient(
          mouse.x, mouse.y, 0,
          mouse.x, mouse.y, mouse.radius * 1.5
        );
        
        // White light effect with transparency
        mouseGlow.addColorStop(0, 'rgba(255, 255, 255, 0.25)');
        mouseGlow.addColorStop(0.3, 'rgba(255, 255, 255, 0.15)');
        mouseGlow.addColorStop(0.6, 'rgba(255, 255, 255, 0.08)');
        mouseGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
        
        // Draw the mouse glow effect
        ctx.fillStyle = mouseGlow;
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, mouse.radius * 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Draw subtle ambient light rays
      const rayGradient = ctx.createRadialGradient(
        canvas.width * 0.5, canvas.height * 0.3, 0,
        canvas.width * 0.5, canvas.height * 0.3, canvas.width * 0.7
      );
      
      rayGradient.addColorStop(0, `rgba(100, 120, 255, ${pulseOpacity})`);
      rayGradient.addColorStop(0.5, `rgba(70, 90, 180, ${pulseOpacity * 0.5})`);
      rayGradient.addColorStop(1, 'rgba(0, 0, 30, 0)');
      
      ctx.fillStyle = rayGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Sort particles by z-index for proper depth rendering
      particlesArray.sort((a, b) => b.z - a.z);
      
      // Update and draw particles
      for (let i = 0; i < particlesArray.length; i++) {
        particlesArray[i].update();
      }
      
      // Connect particles with premium lines
      connect();
      
      // Continue animation loop
      animationFrameId = requestAnimationFrame(animate);
    }
    
    init();
    animate();
    
    // Mark as loaded once particles are initialized
    setIsLoaded(true);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', () => {});
      window.removeEventListener('touchmove', () => {});
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <motion.canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: isLoaded ? 1 : 0 }}
      transition={{ duration: 1.2 }}
      style={{ background: '#000000' }}
    />
  );
};

export default ParticleBackground; 